import { useMemo } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { Amplify } from 'aws-amplify';
import { AppContext } from '@@/contexts/AppContext';
import { config as envConfig } from '@@/configuration/env-config';
import React from 'react';
import LoginPage from '@@/products/portal/pages/login-page';
import ContactUsModal from '@@/ui/widgets/contact-us-modal';
import OpenCaseModal from '@@/ui/widgets/open-case-modal';
import { GlobalProvider } from '@@/contexts/GlobalProvider';
import { Alert, AlertDescription, AlertTitle } from '@@/ui/primitives';

const configureAmplify = () => {
  console.log('Configuring Amplify...');
  const userPoolConfig = JSON.parse(localStorage.getItem('cxp-user-pool-config') ?? '{}');
  const authConfig = {
    aws_cognito_region: envConfig.auth?.region,
    aws_project_region: envConfig.auth?.region,
    aws_user_pools_id: userPoolConfig.userPoolId,
    aws_user_pools_web_client_id: userPoolConfig.clientId,
    oauth: envConfig.auth?.oauth
  };
  Amplify.configure(authConfig);
  const existingConfig = Amplify.getConfig();

  // Configure API endpoints
  Amplify.configure({
    ...existingConfig,
    API: {
      ...existingConfig.API,
      REST: {
        ...existingConfig.API?.REST,
        AccessManagementApi: {
          endpoint: envConfig.accessManagementApi,
          region: envConfig.auth?.region
        },
        ConsoleApi: {
          endpoint: envConfig.api ?? '',
          region: envConfig.auth?.region
        },
        AcgrApi: {
          endpoint: envConfig.acgrApi,
          region: envConfig.auth?.region
        },
        QicApi: {
          endpoint: envConfig.qicApi,
          region: envConfig.auth?.region
        },
        CenteneApi: {
          endpoint: envConfig.centeneApi,
          region: envConfig.auth?.region
        },
        CommonApi: {
          endpoint: envConfig.commonApi,
          region: envConfig.auth?.region
        },
        PbrApi: {
          endpoint: envConfig.pbrApi,
          region: envConfig.auth?.region
        },
        BulkEditApi: {
          endpoint: envConfig.bulkEditApi,
          region: envConfig.auth?.region
        },
        FreshdeskApi: {
          endpoint: envConfig.freshdeskApi,
          region: envConfig.auth?.region
        },
        ...(envConfig.casesApi && {
          CasesApi: {
            endpoint: envConfig.casesApi,
            region: envConfig.auth?.region
          }
        }),
        ConceptApi: {
          endpoint: envConfig.conceptApi,
          region: envConfig.auth?.region
        },
        GraclApi: {
          endpoint: envConfig.graclApi,
          region: envConfig.auth?.region
        },
        InsightsApi: {
          endpoint: envConfig.insightsApi,
          region: envConfig.auth?.region
        },
        CampaignsApi: {
          endpoint: envConfig.campaignsApi,
          region: envConfig.auth?.region
        },
        DfcApi: {
          endpoint: envConfig.dfcApi,
          region: envConfig.auth?.region
        },
        OutageNotificationsApi: {
          endpoint: envConfig.outageNotificationsApi,
          region: envConfig.auth?.region
        },
        KnowledgeManagementApi: {
          endpoint: envConfig.knowledgeManagementApi,
          region: envConfig.auth?.region
        }
      }
    }
  });

  return Amplify.getConfig();
};

export default function AppProvider({ children }: { children: React.ReactNode }) {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);

  const config = configureAmplify();

  const userAuthenticated = useMemo(() => authStatus !== 'unauthenticated', [authStatus]);

  // Loading and error states
  if (!config?.API || !config?.Auth) {
    return (
      <div className='flex justify-center items-center h-full p-20'>
        <Alert>
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Error loading application configuration. Please try again or contact support.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  //console.log('Amplify configuration:', config);
  //console.log('authStatus:', authStatus);

  // Render application
  return (
    <AppContext.Provider value={config}>
      <GlobalProvider>
        {userAuthenticated ? children : <LoginPage />}
        {userAuthenticated && <ContactUsModal />}
        {userAuthenticated && <OpenCaseModal />}
      </GlobalProvider>
    </AppContext.Provider>
  );
}
